import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const translations = {
  en: {
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.pricing': 'Pricing',
    'nav.login': 'Client Login',
    'nav.signup': 'Sign Up',
    'hero.title': 'Iron Hawk Ghana',
    'hero.subtitle': 'Premier drone solutions for aerial photography, mapping, inspections, and more.',
    'hero.cta': 'Request a Service',
    'services.title': 'Our Services',
    'services.subtitle': 'Innovative aerial solutions across various industries',
    'pricing.title': 'Transparent Pricing',
    'pricing.subtitle': 'Competitive rates reflecting quality and expertise',
    'pricing.per_hour': 'per hour',
    'footer.rights': 'All rights reserved.',
    'footer.contact': 'Contact Us',
    // Services
    'service.photo.title': 'Aerial Photography & Videography',
    'service.photo.desc': 'Capturing stunning visuals for real estate, marketing, and tourism.',
    'service.event.title': 'Event Coverage',
    'service.event.desc': 'Documenting weddings, parties, concerts, sports events, and festivals.',
    'service.map.title': 'Surveying & Mapping',
    'service.map.desc': 'Providing 2D/3D maps and construction site monitoring.',
    'service.inspect.title': 'Infrastructure Inspections',
    'service.inspect.desc': 'Conducting thorough inspections of power lines, pipelines, wind turbines, and bridges.',
    'service.agri.title': 'Agricultural Monitoring',
    'service.agri.desc': 'Offering crop health analysis, irrigation planning, and pest detection.',
    'service.rescue.title': 'Search & Rescue',
    'service.rescue.desc': 'Assisting in disaster assessment and locating missing persons.',
    'pricing.starting_from': 'Starting from',
    'pricing.standard_rates': 'Standard Rates',
    'benefits.why_choose': 'Why Choose Iron Hawk Ghana?',
    'benefits.certified': 'Certified Pilots',
    'benefits.certified_desc': 'Licensed professionals with extensive flight experience',
    'benefits.equipment': 'Advanced Equipment',
    'benefits.equipment_desc': 'Latest drone technology for superior results',
    'benefits.support': '24/7 Support',
    'benefits.support_desc': 'Round-the-clock customer service and assistance',
    'benefits.highResolution.title': 'High Resolution Output',
    'benefits.highResolution.description': '4K video and high-resolution photography',
    'benefits.certifiedPilots.title': 'Certified Pilots',
    'benefits.certifiedPilots.description': 'Licensed professionals with extensive flight experience',
    'benefits.fastTurnaround.title': 'Fast Turnaround',
    'benefits.fastTurnaround.description': 'Quick delivery of processed footage and data',
    // Service Request Form
    'form.backToDashboard': 'Back to Dashboard',
    'form.newRequest': 'New Service Request',
    'form.subtitle': 'Fill in the details below to request drone services.',
    'form.selectServices': 'Select Services',
    'form.additionalDetails': 'Additional Details',
    'form.additionalDetailsLabel': 'Tell us more about your project',
    'form.additionalDetailsPlaceholder': 'Please provide any additional details about your service request, such as project timeline, specific requirements, or special instructions...',
    'form.contactLocation': 'Contact & Location',
    'form.emailAddress': 'Email Address',
    'form.phoneNumber': 'Phone Number',
    'form.streetAddress': 'Street Address',
    'form.city': 'City',
    'form.country': 'Country',
    'form.saveDraft': 'Save Draft',
    'form.submitRequest': 'Submit Request',
    'form.processing': 'Processing...',
    'form.selectOneService': 'Please select at least one service',
    'form.savingDraft': 'Saving draft...',
    'form.submittingRequest': 'Submitting request...',
    'form.confirmationEmailSent': 'Confirmation email sent!',
    'form.requestSubmitted': 'Request submitted successfully!',
    'form.draftSaved': 'Draft saved successfully!',
    'form.requestFailed': 'Failed to process request. Please try again.',
    'form.emailSubject': 'Service Request Received - Iron Hawk Ghana',
    'form.emailThankYou': 'Thank you for your request!',
    'form.emailDear': 'Dear',
    'form.emailReceived': 'We have received your request for the following services:',
    'form.emailReview': 'Our team will review your details and contact you shortly at',
    'form.emailBestRegards': 'Best regards,',
    'form.emailTeam': 'Iron Hawk Ghana Team',
    'form.addressPlaceholder': 'House No, Street Name...',
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',
    'dashboard.newRequest': 'New Service Request',
    'dashboard.totalRequests': 'Total Requests',
    'dashboard.pendingInvoices': 'Pending Invoices',
    'dashboard.paidInvoices': 'Paid Invoices',
    'dashboard.invoices': 'Invoices',
    'dashboard.noInvoices': 'No invoices yet',
    'dashboard.invoicesAppear': 'Invoices will appear here after admin approval',
    'dashboard.invoice': 'Invoice',
    'dashboard.services': 'Services',
    'dashboard.pay': 'Pay',
    'dashboard.reject': 'Reject',
    'dashboard.paid': 'Paid',
    'dashboard.recentRequests': 'Recent Requests',
    'dashboard.noRequests': 'No requests yet',
    'dashboard.getStarted': 'Get started by creating your first service request',
    'dashboard.createRequest': 'Create Request',
    // Invoice
    'invoice.rejecting': 'Rejecting invoice...',
    'invoice.rejected': 'Invoice rejected successfully',
    'invoice.rejectFailed': 'Failed to reject invoice',
    'invoice.selectPaymentMethod': 'Please select a payment method',
    'invoice.processing': 'Processing payment...',
    'invoice.instructionsSent': 'Payment instructions sent to your email',
    'invoice.paymentFailed': 'Payment processing failed',
    'invoice.selectPayment': 'Select Payment Method',
    'invoice.amount': 'Amount',
    'invoice.proceed': 'Proceed to Payment',
    // Payment
    'payment.creditCard': 'Credit/Debit Card (Stripe)',
    'payment.mtn': 'MTN Mobile Money',
    'payment.vodafone': 'Vodafone Cash',
    'payment.airteltigo': 'AirtelTigo Money',
    // Contact Us
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch with our team',
    'contact.getInTouch': 'Get In Touch',
    'contact.description': 'Have questions about our drone services? We\'re here to help. Send us a message and we\'ll respond as soon as possible.',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.location': 'Location',
    'contact.sendMessage': 'Send us a Message',
    'contact.name': 'Name',
    'contact.subject': 'Subject',
    'contact.message': 'Message',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.messageSent': 'Message sent successfully!',
    'contact.messageFailed': 'Failed to send message. Please try again.',
    'contact.newMessage': 'New Contact Form Submission',
  },
  fr: {
    'nav.home': 'Accueil',
    'nav.services': 'Services',
    'nav.pricing': 'Tarification',
    'nav.login': 'Connexion Client',
    'nav.signup': 'S\'inscrire',
    'hero.title': 'Iron Hawk Ghana',
    'hero.subtitle': 'Solutions de drones de premier plan pour la photographie aérienne, la cartographie, les inspections, et plus encore.',
    'hero.cta': 'Demander un Service',
    'services.title': 'Nos Services',
    'services.subtitle': 'Solutions aériennes innovantes pour diverses industries',
    'pricing.title': 'Tarification Transparente',
    'pricing.subtitle': 'Des tarifs compétitifs reflétant la qualité et l\'expertise',
    'pricing.per_hour': 'par heure',
    'footer.rights': 'Tous droits réservés.',
    'footer.contact': 'Contactez-nous',
    // Services
    'service.photo.title': 'Photographie et Vidéographie Aérienne',
    'service.photo.desc': 'Capture de visuels époustouflants pour l\'immobilier, le marketing et le tourisme.',
    'service.event.title': 'Couverture d\'Événements',
    'service.event.desc': 'Documentation de mariages, fêtes, concerts, événements sportifs et festivals.',
    'service.map.title': 'Arpentage et Cartographie',
    'service.map.desc': 'Fourniture de cartes 2D/3D et surveillance de chantiers de construction.',
    'service.inspect.title': 'Inspections d\'Infrastructures',
    'service.inspect.desc': 'Inspections approfondies des lignes électriques, pipelines, éoliennes et ponts.',
    'service.agri.title': 'Surveillance Agricole',
    'service.agri.desc': 'Analyse de la santé des cultures, planification de l\'irrigation et détection des ravageurs.',
    'service.rescue.title': 'Recherche et Sauvetage',
    'service.rescue.desc': 'Aide à l\'évaluation des catastrophes et à la localisation des personnes disparues.',
    'pricing.starting_from': 'À partir de',
    'pricing.standard_rates': 'Tarifs Standard',
    'benefits.why_choose': 'Pourquoi Choisir Iron Hawk Ghana?',
    'benefits.certified': 'Pilotes Certifiés',
    'benefits.certified_desc': 'Professionnels agréés avec une vaste expérience de vol',
    'benefits.equipment': 'Équipement Avancé',
    'benefits.equipment_desc': 'Dernière technologie de drone pour des résultats supérieurs',
    'benefits.support': 'Support 24/7',
    'benefits.support_desc': 'Service client et assistance 24h/24',
    'benefits.highResolution.title': 'Sortie Haute Résolution',
    'benefits.highResolution.description': 'Vidéo 4K et photographie haute résolution',
    'benefits.certifiedPilots.title': 'Pilotes Certifiés',
    'benefits.certifiedPilots.description': 'Professionnels agréés avec une vaste expérience de vol',
    'benefits.fastTurnaround.title': 'Livraison Rapide',
    'benefits.fastTurnaround.description': 'Livraison rapide des séquences et données traitées',
    // Service Request Form
    'form.backToDashboard': 'Retour au Tableau de Bord',
    'form.newRequest': 'Nouvelle Demande de Service',
    'form.subtitle': 'Remplissez les détails ci-dessous pour demander des services de drone.',
    'form.selectServices': 'Sélectionner les Services',
    'form.additionalDetails': 'Détails Supplémentaires',
    'form.additionalDetailsLabel': 'Parlez-nous de votre projet',
    'form.additionalDetailsPlaceholder': 'Veuillez fournir des détails supplémentaires sur votre demande de service, tels que le calendrier du projet, les exigences spécifiques ou les instructions spéciales...',
    'form.contactLocation': 'Contact et Localisation',
    'form.emailAddress': 'Adresse Email',
    'form.phoneNumber': 'Numéro de Téléphone',
    'form.streetAddress': 'Adresse',
    'form.city': 'Ville',
    'form.country': 'Pays',
    'form.saveDraft': 'Enregistrer le Brouillon',
    'form.submitRequest': 'Soumettre la Demande',
    'form.processing': 'Traitement en cours...',
    'form.selectOneService': 'Veuillez sélectionner au moins un service',
    'form.savingDraft': 'Enregistrement du brouillon...',
    'form.submittingRequest': 'Soumission de la demande...',
    'form.confirmationEmailSent': 'Email de confirmation envoyé!',
    'form.requestSubmitted': 'Demande soumise avec succès!',
    'form.draftSaved': 'Brouillon enregistré avec succès!',
    'form.requestFailed': 'Échec du traitement de la demande. Veuillez réessayer.',
    'form.emailSubject': 'Demande de Service Reçue - Iron Hawk Ghana',
    'form.emailThankYou': 'Merci pour votre demande!',
    'form.emailDear': 'Cher',
    'form.emailReceived': 'Nous avons reçu votre demande pour les services suivants:',
    'form.emailReview': 'Notre équipe examinera vos détails et vous contactera sous peu au',
    'form.emailBestRegards': 'Cordialement,',
    'form.emailTeam': 'Équipe Iron Hawk Ghana',
    'form.addressPlaceholder': 'Numéro de Maison, Nom de Rue...',
    // Dashboard
    'dashboard.title': 'Tableau de Bord',
    'dashboard.welcome': 'Bienvenue',
    'dashboard.newRequest': 'Nouvelle Demande de Service',
    'dashboard.totalRequests': 'Demandes Totales',
    'dashboard.pendingInvoices': 'Factures en Attente',
    'dashboard.paidInvoices': 'Factures Payées',
    'dashboard.invoices': 'Factures',
    'dashboard.noInvoices': 'Aucune facture pour le moment',
    'dashboard.invoicesAppear': 'Les factures apparaîtront ici après l\'approbation de l\'administrateur',
    'dashboard.invoice': 'Facture',
    'dashboard.services': 'Services',
    'dashboard.pay': 'Payer',
    'dashboard.reject': 'Rejeter',
    'dashboard.paid': 'Payé',
    'dashboard.recentRequests': 'Demandes Récentes',
    'dashboard.noRequests': 'Aucune demande pour le moment',
    'dashboard.getStarted': 'Commencez par créer votre première demande de service',
    'dashboard.createRequest': 'Créer une Demande',
    // Invoice
    'invoice.rejecting': 'Rejet de la facture...',
    'invoice.rejected': 'Facture rejetée avec succès',
    'invoice.rejectFailed': 'Échec du rejet de la facture',
    'invoice.selectPaymentMethod': 'Veuillez sélectionner un mode de paiement',
    'invoice.processing': 'Traitement du paiement...',
    'invoice.instructionsSent': 'Instructions de paiement envoyées à votre email',
    'invoice.paymentFailed': 'Échec du traitement du paiement',
    'invoice.selectPayment': 'Sélectionner le Mode de Paiement',
    'invoice.amount': 'Montant',
    'invoice.proceed': 'Procéder au Paiement',
    // Payment
    'payment.creditCard': 'Carte de Crédit/Débit (Stripe)',
    'payment.mtn': 'MTN Mobile Money',
    'payment.vodafone': 'Vodafone Cash',
    'payment.airteltigo': 'AirtelTigo Money',
    // Contact Us
    'contact.title': 'Contactez-nous',
    'contact.subtitle': 'Entrez en contact avec notre équipe',
    'contact.getInTouch': 'Prenez Contact',
    'contact.description': 'Vous avez des questions sur nos services de drones? Nous sommes là pour vous aider. Envoyez-nous un message et nous vous répondrons dès que possible.',
    'contact.email': 'Email',
    'contact.phone': 'Téléphone',
    'contact.location': 'Emplacement',
    'contact.sendMessage': 'Envoyez-nous un Message',
    'contact.name': 'Nom',
    'contact.subject': 'Sujet',
    'contact.message': 'Message',
    'contact.send': 'Envoyer le Message',
    'contact.sending': 'Envoi en cours...',
    'contact.messageSent': 'Message envoyé avec succès!',
    'contact.messageFailed': 'Échec de l\'envoi du message. Veuillez réessayer.',
    'contact.newMessage': 'Nouvelle Soumission de Formulaire de Contact',
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string) => {
    // @ts-ignore
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
